'use client';

import { useState } from 'react';
import ComboBox, { ComboBoxOptionsSlot } from '@pixum/combobox';
import MenuItem, { LeadingType } from '@pixum/menu-item';
import type { RevocableOrder } from 'data-layer';
import { formatDateByLocale, type LocaleIso } from 'utils';
import styles from './OrdersMultiSelection.module.css';
import { MultiSelectionFallbackContent } from './MultiSelectionFallbackContent';

interface OrdersMultiSelectProps {
  revocableOrders: RevocableOrder[];
  selectedOrderIds: number[];
  placeholder: string;
  label: string;
  orderItemLabel: string;
  fallbackText: string;
  summaryCounts: string;
  locale: string;
  disabled?: boolean;
  loading?: boolean;
  onChange?: (selectedOrderIds: number[]) => void;
  helperText?: string;
  hasError?: boolean;
}

/**
 * Multi-select ComboBox for choosing which orders to include in a cancellation request.
 * Displays each revocable order with its delivery date and total amount.
 * Collapses the display value to a count summary when more than two orders are selected.
 *
 * @param props.revocableOrders - All orders eligible for revocation, fetched from PSI
 * @param props.selectedOrderIds - Currently selected order IDs controlled by the parent
 * @param props.placeholder - Placeholder text shown when no orders are selected
 * @param props.label - Accessible label for the ComboBox and its options list
 * @param props.orderItemLabel - Label template per order item; supports {orderId} placeholder
 * @param props.fallbackText - Message shown when no revocable orders are available
 * @param props.summaryCounts - Summary template for >2 selections; supports {count} placeholder
 * @param props.disabled - Disables the ComboBox when the user is not authenticated
 * @param props.loading - Shows a loading state while orders are being refreshed
 * @param props.onChange - Callback fired with the updated array of selected order IDs
 */
export function OrdersMultiSelect(props: OrdersMultiSelectProps) {
  const {
    revocableOrders,
    selectedOrderIds,
    label,
    placeholder,
    fallbackText,
    summaryCounts,
    locale,
    disabled,
    loading,
    onChange,
    orderItemLabel,
    helperText,
    hasError,
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const toggleOrder = (orderId: number) => {
    const next = selectedOrderIds.includes(orderId)
      ? selectedOrderIds.filter(id => id !== orderId)
      : [...selectedOrderIds, orderId];

    onChange?.(next);
  };

  const formatValue = (): string => {
    if (selectedOrderIds.length > 2) {
      return summaryCounts.replace('{count}', String(selectedOrderIds.length));
    }

    return revocableOrders
      .filter(o => selectedOrderIds.includes(o.userOrderId))
      .map(o => `#${o.userOrderId}`)
      .join(', ');
  };

  const options = (
    <ComboBoxOptionsSlot
      className={styles.orders}
      aria-multiselectable="true"
      aria-label={label}
      tag="ul"
      role="listbox"
    >
      {revocableOrders.map(order => {
        const selected = selectedOrderIds.includes(order.userOrderId);

        return (
          <MenuItem
            key={order.userOrderId}
            role="option"
            tag="li"
            text={orderItemLabel.replace('{orderId}', String(order.userOrderId))}
            subtext={formatDateByLocale(order.deliveryDate, locale as LocaleIso, { timeZone: 'UTC' })}
            subItemProps={{ text: order.totals.sumFormatted }}
            leadingProps={{ type: LeadingType.CheckBox }}
            selected={selected}
            aria-selected={selected}
            onClick={() => toggleOrder(order.userOrderId)}
          />
        );
      })}
    </ComboBoxOptionsSlot>
  );

  const fallback = (
    <ComboBoxOptionsSlot tag="div" className={styles.fallback} aria-live="polite">
      <MultiSelectionFallbackContent text={fallbackText}></MultiSelectionFallbackContent>
    </ComboBoxOptionsSlot>
  );

  return (
    <ComboBox
      disabled={disabled}
      loading={loading}
      label={label}
      headline={label}
      placeholder={placeholder}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onClick={() => setIsOpen(prev => !prev)}
      options={[]}
      value={formatValue()}
      withSearch={false}
      optionsSlot={revocableOrders.length > 0 ? options : fallback}
      helperText={helperText}
      hasError={hasError}
    />
  );
}
